import React, { useEffect, useState, useRef, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { compressImageIfNeeded } from "@/lib/imageCompress";
import { toast } from "sonner";
import { Upload, FileText, Image as ImageIcon, Trash2 } from "lucide-react";

const MAX_BYTES = 15 * 1024 * 1024;

function Thumb({ fileId, filename }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url = null;
    let cancelled = false;
    api.get(`/uploads/${fileId}/download`, { responseType: "blob" })
      .then((res) => {
        if (cancelled) return;
        url = URL.createObjectURL(res.data);
        setSrc(url);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [fileId]);
  if (!src) {
    return <div className="w-16 h-16 border border-slate-200 bg-slate-50 animate-pulse" />;
  }
  return (
    <img
      src={src}
      alt={filename}
      title={filename}
      data-testid={`file-thumb-${fileId}`}
      className="w-16 h-16 object-cover border border-slate-200 bg-slate-50"
    />
  );
}

export default function FileAttachments({ module, recordId }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(() => {
    api.get(`/${module}/${recordId}/files`)
      .then((r) => setFiles(r.data || []))
      .catch((e) => toast.error(formatApiError(e)));
  }, [module, recordId]);

  useEffect(() => { if (recordId) load(); }, [recordId, module, load]);

  const uploadFiles = async (fileList) => {
    const arr = Array.from(fileList || []);
    if (arr.length === 0) return;
    setUploading(true);
    let ok = 0, fail = 0;
    for (const original of arr) {
      const file = await compressImageIfNeeded(original);
      if (file !== original) {
        const savedKB = ((original.size - file.size) / 1024).toFixed(0);
        toast.message(`Compressed ${original.name} (saved ~${savedKB}KB)`);
      }
      if (file.size > MAX_BYTES) {
        toast.error(`${file.name}: ${(file.size / 1048576).toFixed(1)} MB exceeds the 15 MB limit — please compress`);
        fail += 1;
        continue;
      }
      try {
        const form = new FormData();
        form.append("file", file);
        form.append("related_module", module);
        form.append("related_id", recordId);
        await api.post("/uploads", form);
        ok += 1;
      } catch (err) {
        toast.error(`${file.name}: ${formatApiError(err)}`);
        fail += 1;
      }
    }
    if (ok > 0) toast.success(`${ok} file${ok === 1 ? "" : "s"} uploaded${fail ? ` · ${fail} failed` : ""}`);
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
    load();
  };

  const onInput = (e) => uploadFiles(e.target.files);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files?.length) uploadFiles(e.dataTransfer.files);
  };

  const download = async (f) => {
    try {
      const res = await api.get(`/uploads/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = f.original_filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (f) => {
    if (!window.confirm(`Delete "${f.original_filename}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/uploads/${f.id}`);
      toast.success("File removed");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const images = files.filter((f) => f.content_type?.startsWith("image/"));
  const others = files.filter((f) => !f.content_type?.startsWith("image/"));

  return (
    <div
      className={`pt-3 border-t border-slate-200 ${dragOver ? "bg-sky-50" : ""}`}
      data-testid="file-attachments"
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
          Attachments <span className="text-slate-400">({files.length})</span>
        </div>
        <label className="cursor-pointer text-xs text-sky-700 hover:text-sky-900 flex items-center gap-1">
          <Upload className="w-3.5 h-3.5" />
          {uploading ? "Uploading…" : "Upload · drag & drop supported"}
          <input
            ref={inputRef} type="file" className="hidden" multiple
            onChange={onInput}
            data-testid="file-upload-input"
            accept="image/*,audio/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
          />
        </label>
      </div>

      {images.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3" data-testid="file-thumb-strip">
          {images.map((f) => (
            <button key={f.id} onClick={() => download(f)} className="relative group" title={f.original_filename}>
              <Thumb fileId={f.id} filename={f.original_filename} />
              <span
                onClick={(e) => { e.stopPropagation(); remove(f); }}
                className="absolute top-0.5 right-0.5 bg-white/90 border border-slate-200 p-0.5 opacity-0 group-hover:opacity-100 transition-opacity hover:text-red-600"
                data-testid={`file-thumb-delete-${f.id}`}
              >
                <Trash2 className="w-3 h-3" />
              </span>
            </button>
          ))}
        </div>
      )}

      {others.length > 0 && (
        <ul className="space-y-1">
          {others.map((f) => (
            <li key={f.id} className="flex items-center gap-2 text-xs group">
              <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <button
                onClick={() => download(f)}
                className="text-slate-700 hover:text-sky-700 truncate flex-1 text-left"
                data-testid={`file-${f.id}`}
              >
                {f.original_filename}
              </button>
              <span className="text-slate-400 tabular-nums text-[10px]">{(f.size / 1024).toFixed(0)}KB</span>
              <button
                onClick={() => remove(f)}
                className="text-slate-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                title="Delete file"
                data-testid={`file-delete-${f.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {files.length === 0 && (
        <div className="text-xs text-slate-400 italic" data-testid="file-empty">
          No files attached. Drag files here or click &ldquo;Upload&rdquo;.
        </div>
      )}
    </div>
  );
}
